import { type Mesh2MotionEngine } from '../Mesh2MotionEngine'
import { ModelPreviewDisplay } from './enums/ModelPreviewDisplay'
import { ProcessStep } from './enums/ProcessStep'
import { TransformSpace } from './enums/TransformSpace'
import { Utility } from './Utilities'
import { ModelCleanupUtility } from './processes/load-model/ModelCleanupUtility'
import { type Bone } from 'three'

export class EventListeners {
  constructor (private readonly bootstrap: Mesh2MotionEngine) {}

  public addEventListeners (): void {
    // monitor theme changes
    this.bootstrap.theme_manager.addEventListener('theme-changed', () => {
      this.bootstrap.regenerate_floor_grid()
    })

    this.bootstrap.load_skeleton_step.addEventListener('skeletonLoaded', () => {
      this.bootstrap.edit_skeleton_step.load_original_armature_from_model(this.bootstrap.load_skeleton_step.armature())
      this.bootstrap.process_step = this.bootstrap.process_step_changed(ProcessStep.EditSkeleton)
    })

    // Listen for skeleton transformation events to update UI and visuals
    // this can happen with undo/redo system
    this.bootstrap.edit_skeleton_step.addEventListener('skeletonTransformed', () => {
      // Update skeleton helper if it exists
      if (this.bootstrap.skeleton_helper !== undefined) {
        this.bootstrap.regenerate_skeleton_helper(this.bootstrap.edit_skeleton_step.skeleton(), 'Skeleton Helper')
        this.bootstrap.sync_skeleton_helper_joint_visibility()
      }

      // Refresh weight painting if in weight painted mode
      if (this.bootstrap.mesh_preview_display_type === ModelPreviewDisplay.WeightPainted) {
        this.bootstrap.regenerate_weight_painted_preview_mesh()
      }
    })

    this.bootstrap.edit_skeleton_step.addEventListener('mirrorModeChanged', () => {
      this.bootstrap.sync_skeleton_helper_joint_visibility()
    })

    this.bootstrap.edit_skeleton_step.addEventListener('boneEditModeChanged', () => {
      this.bootstrap.update_edit_bone_interaction_mode()
    })

    // attribution link clicking brings up contributors dialog
    this.bootstrap.ui.dom_attribution_link?.addEventListener('click', (event: MouseEvent) => {
      event.preventDefault()
      this.bootstrap.show_contributors_dialog()
    })

    // Learn link clicking brings up learning resources
    this.bootstrap.ui.dom_learn_link?.addEventListener('click', (event: MouseEvent) => {
      event.preventDefault()
      this.bootstrap.show_learning_resources_dialog()
    })

    // listen for view helper changes
    document.getElementById('view-control-hitbox')?.addEventListener('pointerdown', (event: PointerEvent) => {
      if (this.bootstrap.view_helper?.handleClick(event) === true) {
        event.stopPropagation()
        event.preventDefault()
      }
    })

    this.bootstrap.renderer.domElement.addEventListener('mousemove', (event: MouseEvent) => {
      if (this.bootstrap.is_transform_controls_dragging) {
        this.bootstrap.handle_transform_controls_moving()
      }

      if (this.bootstrap.is_mesh_drag_mode_dragging) {
        this.bootstrap.handle_mesh_drag_mode_mouse_move(event)
      }

      // edit skeleton step logic that deals with hovering over bones
      if (this.bootstrap.process_step === ProcessStep.EditSkeleton) {
        this.bootstrap.edit_skeleton_step.calculate_bone_hover_effect(event, this.bootstrap.camera, this.bootstrap.transform_controls_hover_distance)
      }
    })

    this.bootstrap.renderer.domElement.addEventListener('mousedown', (event: MouseEvent) => {
      const use_mesh_drag_mode =
        this.bootstrap.process_step === ProcessStep.EditSkeleton &&
        this.bootstrap.edit_skeleton_step.is_mesh_drag_placement_enabled()

      if (use_mesh_drag_mode) {
        this.bootstrap.handle_mesh_drag_mode_mouse_down(event)
      } else {
        this.bootstrap.handle_transform_controls_mouse_down(event)
      }

      // update UI with current bone name
      const selected_bone = this.bootstrap.edit_skeleton_step.get_currently_selected_bone()
      if (this.bootstrap.ui.dom_selected_bone_label !== null &&
        selected_bone !== null) {
        this.bootstrap.ui.dom_selected_bone_label.innerHTML = selected_bone.name
      }
    }, false)

    document.addEventListener('mouseup', () => {
      this.bootstrap.handle_mesh_drag_mode_mouse_up()
    })

    // custom event listeners for the transform controls.
    // we can know about the "mouseup" event with this
    this.bootstrap.transform_controls?.addEventListener('dragging-changed', (event: any) => {
      this.bootstrap.is_transform_controls_dragging = event.value
      this.bootstrap.enable_orbit_controls(!event.value)

      // Store undo state when we start dragging (event.value = true)
      if (event.value && this.bootstrap.process_step === ProcessStep.EditSkeleton) {
        this.bootstrap.edit_skeleton_step.store_bone_state_for_undo()

        // Record children's initial world positions for independent bone movement
        if (this.bootstrap.edit_skeleton_step.independent_bone_movement.is_enabled()) {
          const selected_bone = this.bootstrap.transform_controls.object
          if (selected_bone !== undefined && selected_bone !== null) {
            const mirror_bone = this.bootstrap.edit_skeleton_step.is_mirror_mode_enabled()
              ? this.bootstrap.edit_skeleton_step.find_mirror_bone(selected_bone as Bone)
              : undefined
            this.bootstrap.edit_skeleton_step.independent_bone_movement.record_drag_start(selected_bone as Bone, mirror_bone)
          }
        }
      }

      if (!event.value &&
        this.bootstrap.process_step === ProcessStep.EditSkeleton &&
        this.bootstrap.transform_controls.getMode() === 'translate' &&
        this.bootstrap.edit_skeleton_step.independent_bone_movement.is_enabled()) {
        const selected_bone = this.bootstrap.transform_controls.object
        if (selected_bone !== undefined && selected_bone !== null) {
          const mirror_bone = this.bootstrap.edit_skeleton_step.is_mirror_mode_enabled()
            ? this.bootstrap.edit_skeleton_step.find_mirror_bone(selected_bone as Bone)
            : undefined
          this.bootstrap.edit_skeleton_step.independent_bone_movement.finalize_drop(selected_bone as Bone, mirror_bone)
        }
      }

      // if we stopped dragging, that means a mouse up.
      // if we are editing skeleton and viewing weight painted mesh, refresh the weight painting
      if (this.bootstrap.process_step === ProcessStep.EditSkeleton &&
        this.bootstrap.mesh_preview_display_type === ModelPreviewDisplay.WeightPainted) {
        this.bootstrap.regenerate_weight_painted_preview_mesh()
      }

      // Auto-keyframe: notify the AnimationsListing step on drag-end so it
      // can bake the just-edited pose into the active clip if the user has
      // auto-keyframe mode enabled.
      if (!event.value && this.bootstrap.process_step === ProcessStep.AnimationsListing) {
        this.bootstrap.animations_listing_step.handle_transform_controls_drag_end()
      }
    })

    this.bootstrap.load_model_step.addEventListener('modelLoaded', () => {
      this.bootstrap.process_step = this.bootstrap.process_step_changed(ProcessStep.LoadSkeleton)
    })

    this.bootstrap.ui.dom_bind_pose_button?.addEventListener('click', () => {
      this.bootstrap.setup_weight_skinning_config()
      this.bootstrap.process_step_changed(ProcessStep.BindPose)
    })

    // rotate model after loading it in to orient it correctly
    this.bootstrap.ui.dom_rotate_model_x_button?.addEventListener('click', () => {
      this.bootstrap.load_model_step.rotate_model_geometry('x', 90)
    })

    this.bootstrap.ui.dom_rotate_model_y_button?.addEventListener('click', () => {
      this.bootstrap.load_model_step.rotate_model_geometry('y', 90)
    })

    this.bootstrap.ui.dom_rotate_model_z_button?.addEventListener('click', () => {
      this.bootstrap.load_model_step.rotate_model_geometry('z', 90)
    })

    // Reset model position to import state
    this.bootstrap.ui.dom_reset_model_position_button?.addEventListener('click', () => {
      this.bootstrap.load_model_step.reset_model_position()
    })

    // Auto-align model to floor
    this.bootstrap.ui.dom_auto_align_model_button?.addEventListener('click', () => {
      // move the mesh geometry data to the floor
      const mesh_data = this.bootstrap.load_model_step.model_meshes()
      ModelCleanupUtility.move_model_to_floor(mesh_data)

      // move the transform widget to reset it to the new position on the canvas
      const py = mesh_data.position.y
      if (py !== 0) {
        ModelCleanupUtility.translate_model_vertices(mesh_data, 0, py, 0)
        mesh_data.position.setY(0)
      }
    })

    this.bootstrap.ui.dom_show_skeleton_checkbox?.addEventListener('click', (event: MouseEvent) => {
      if (this.bootstrap.skeleton_helper !== undefined) {
        this.bootstrap.skeleton_helper.visible = (event.target as HTMLInputElement).checked
      } else {
        console.warn('Skeleton helper is undefined, so we cannot show it')
      }
    })

    this.bootstrap.ui.dom_export_button?.addEventListener('click', () => {
      const all_clips = this.bootstrap.animations_listing_step.animation_clips()
      const animations_to_export: number[] = this.bootstrap.animations_listing_step.get_animation_indices_to_export()

      this.bootstrap.file_export_step.set_animation_clips_to_export(all_clips, animations_to_export)
      void this.bootstrap.file_export_step.export(
        this.bootstrap.animations_listing_step.active_skinned_meshes(),
        'exported-model',
        this.bootstrap.download_settings
      )
    })

    this.install_batch_zip_export_button()

    // going back to edit skeleton step after skinning
    // this will do a lot of resetting
    this.bootstrap.ui.dom_back_to_edit_skeleton_button?.addEventListener('click', () => {
      this.bootstrap.remove_skinned_meshes_from_scene() // clear any existing skinned meshes
      this.bootstrap.debugging_visual_object = Utility.regenerate_debugging_scene(this.bootstrap.scene)
      this.bootstrap.process_step = this.bootstrap.process_step_changed(ProcessStep.EditSkeleton)

      // reset current bone selection for edit skeleton step
      this.bootstrap.edit_skeleton_step.set_currently_selected_bone(null)

      if (this.bootstrap.ui.dom_selected_bone_label !== null) {
        this.bootstrap.ui.dom_selected_bone_label.innerHTML = 'None'
      }

      // reset the undo/redo system
      this.bootstrap.edit_skeleton_step.clear_undo_history()
    })

    // going back to load skeleton step from edit skeleton step
    this.bootstrap.ui.dom_back_to_load_skeleton_button?.addEventListener('click', () => {
      this.bootstrap.process_step = this.bootstrap.process_step_changed(ProcessStep.LoadSkeleton)
    })

    this.bootstrap.ui.dom_back_to_load_model_button?.addEventListener('click', () => {
      this.bootstrap.process_step = this.bootstrap.process_step_changed(ProcessStep.LoadModel)
    })

    this.bootstrap.ui.dom_transform_type_radio_group?.addEventListener('change', (event: Event) => {
      const radio_button_selected = (event.target as HTMLInputElement | null)?.value ?? null

      if (radio_button_selected === null) {
        console.warn('Null radio button selected for transform type change')
        return
      }

      this.bootstrap.changed_transform_controls_mode(radio_button_selected)
    })

    this.bootstrap.ui.dom_transform_space_radio_group?.addEventListener('change', (event: Event) => {
      const radio_button_selected = (event.target as HTMLInputElement | null)?.value ?? null

      if (radio_button_selected === null) {
        console.warn('Null radio button selected for transform space change')
        return
      }

      this.bootstrap.changed_transform_controls_space(Utility.enum_from_value(radio_button_selected, TransformSpace))
    })

    // changing the 3d model preview while editing the skeleton bones
    this.bootstrap.ui.dom_mesh_preview_group?.addEventListener('change', (event: Event) => {
      const radio_button_selected = (event.target as HTMLInputElement | null)?.value ?? null

      if (radio_button_selected === null) {
        console.warn('Null radio button selected for mesh preview type change')
        return
      }

      if (radio_button_selected === ModelPreviewDisplay.Textured) {
        this.bootstrap.changed_model_preview_display(ModelPreviewDisplay.Textured)
      } else if (radio_button_selected === ModelPreviewDisplay.WeightPainted) {
        this.bootstrap.changed_model_preview_display(ModelPreviewDisplay.WeightPainted)
      } else {
        console.warn(`Unknown mesh preview type selected: ${radio_button_selected}`)
      }
    })

    // Keyboard shortcuts for undo/redo
    document.addEventListener('keydown', (event: KeyboardEvent) => {
      // Only handle keyboard shortcuts when in EditSkeleton step
      if (this.bootstrap.process_step !== ProcessStep.EditSkeleton) {
        return
      }

      // Define undo/redo shortcut conditions
      // Ctrl+Z or Cmd+Z for undo
      // Ctrl+Y, Cmd+Y, Ctrl+Shift+Z, or Cmd+Shift+Z for redo
      const is_undo_shortcut_pressed = (event.ctrlKey || event.metaKey) && event.key === 'z' && !event.shiftKey
      const is_redo_shortcut_pressed = (event.ctrlKey || event.metaKey) && (event.key === 'y' || (event.key === 'z' && event.shiftKey))

      if (is_undo_shortcut_pressed) {
        event.preventDefault()
        this.bootstrap.edit_skeleton_step.undo_bone_transformation()
      }

      if (is_redo_shortcut_pressed) {
        event.preventDefault()
        this.bootstrap.edit_skeleton_step.redo_bone_transformation()
      }
    })
  }

  private install_batch_zip_export_button (): void {
    const export_button = this.bootstrap.ui.dom_export_button
    if (export_button === null || export_button.parentElement === null) return
    if (document.querySelector('#export-batch-zip-button') !== null) return

    const batch_button = document.createElement('button')
    batch_button.id = 'export-batch-zip-button'
    batch_button.className = export_button.className
    batch_button.type = 'button'
    batch_button.disabled = true
    batch_button.setAttribute('data-tippy-content', 'Export selected animations as separate GLB files in one ZIP.')
    batch_button.textContent = 'Batch ZIP'
    export_button.parentElement.appendChild(batch_button)

    const sync_disabled = (): void => {
      batch_button.disabled = this.bootstrap.animations_listing_step.get_animation_indices_to_export().length === 0
    }
    sync_disabled()
    this.bootstrap.ui.dom_animation_clip_list?.addEventListener('change', sync_disabled)
    this.bootstrap.ui.dom_animation_clip_list?.addEventListener('click', () => window.setTimeout(sync_disabled, 0))

    batch_button.addEventListener('click', () => {
      const all_clips = this.bootstrap.animations_listing_step.animation_clips()
      const animations_to_export: number[] = this.bootstrap.animations_listing_step.get_animation_indices_to_export()
      this.bootstrap.file_export_step.set_animation_clips_to_export(all_clips, animations_to_export)
      void this.bootstrap.file_export_step.export_batch_zip(
        this.bootstrap.animations_listing_step.active_skinned_meshes(),
        'selected-animations',
        this.bootstrap.download_settings
      )
    })
  }
}
